import React, { useCallback, useRef } from "react";
import {
  User, Users, Coffee, Utensils, Dice5,
  Smile, Scale, Flame,
  MessageCircle,
  Check, HelpCircle
} from "lucide-react";
import { FEEL_OPTIONS } from "../../hooks/useSimplifyPreferences";
import './ModalComponents.css';

/**
 * SimplifyPreferencesPanel - Renders the preferences selection UI
 *
 * Props:
 * - preferences: object - Current preferences state {players, feel, energy, time, familiarity, useCollection}
 * - setPreferences: (prefs) => void - Set all preferences at once
 * - setFamiliarity: (familiarity) => void - Set familiarity preference specifically
 * - playerCountOptions: number[] - Array of available player counts (used for max bound)
 * - timeOptions: {value, label}[] - Array of playtime options
 * - energyOptions: {value, label}[] - Array of energy options
 * - feelOptions: {value, label}[] - Array of interaction options
 * - onNext: () => void - Handler for the Next button
 * - onShowHelp: () => void - Handler to show the help modal
 * - _hasSelectedGames: boolean - (unused, preserved for prop compat)
 */

const MIN_PLAYERS = 1;
const MAX_PLAYERS = 8;

const TIME_ICONS = {
  short: <Coffee size={18} aria-hidden="true" />,
  medium: <Utensils size={18} aria-hidden="true" />,
  long: <Dice5 size={18} aria-hidden="true" />,
};

const ENERGY_ICONS = {
  low: <Smile size={18} aria-hidden="true" />,
  medium: <Scale size={18} aria-hidden="true" />,
  high: <Flame size={18} aria-hidden="true" />,
};

/** Reusable icon-option button with selected state and check mark */
const IconOptionButton = ({ icon, label, isSelected, onClick, ariaLabel, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel || label}
    aria-pressed={isSelected}
    className={`spp-option-btn ${isSelected ? "spp-option-btn--selected" : ""} ${className}`}
  >
    <span className="spp-option-btn__icon">{icon}</span>
    <span className="spp-option-btn__label">{label}</span>
    {isSelected && (
      <span className="spp-option-btn__check" aria-hidden="true">
        <Check size={12} strokeWidth={3} />
      </span>
    )}
  </button>
);

const InteractionSlider = ({ options, value, onChange, ariaLabelledBy }) => {
  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const buttonRefs = useRef([]);

  const handleKeyDown = (e, idx) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      if (idx > 0) {
        onChange(options[idx - 1].value);
        buttonRefs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      if (idx < options.length - 1) {
        onChange(options[idx + 1].value);
        buttonRefs.current[idx + 1]?.focus();
      }
    } else if (e.key === "Home") {
      e.preventDefault();
      onChange(options[0].value);
      buttonRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      onChange(options[options.length - 1].value);
      buttonRefs.current[options.length - 1]?.focus();
    }
  };

  const fillWidth = selectedIndex >= 1 ? `${(selectedIndex / (options.length - 1)) * 100}%` : "0%";

  return (
    <div className="spp-feel-slider" role="group" aria-labelledby={ariaLabelledBy}>
      <div className="spp-feel-slider__track" aria-hidden="true">
        <div className="spp-feel-slider__fill" style={{ width: fillWidth }} />
        {options.map((option, idx) => {
          const isSelected = selectedIndex === idx;
          const isLabelVisible = idx === 0 || idx === 2 || idx === options.length - 1;
          const position = isLabelVisible
            ? (idx === 0 || idx === options.length - 1 ? "below" : "above")
            : null;
          return (
            <button
              key={option.value}
              type="button"
              ref={(el) => { buttonRefs.current[idx] = el; }}
              className={`spp-feel-slider__point ${isSelected ? "spp-feel-slider__point--selected" : ""}`}
              style={{ left: `${(idx / (options.length - 1)) * 100}%` }}
              aria-pressed={isSelected}
              aria-label={`Interaction: ${option.label}`}
              onClick={() => onChange(option.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
            >
              {isLabelVisible && (
                <span
                  className={`spp-feel-slider__label spp-feel-slider__label--${position} spp-feel-slider__label--visible`}
                >
                  {option.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SimplifyPreferencesPanel = ({
  preferences,
  setPreferences,
  setFamiliarity: _setFamiliarity,
  _playerCountOptions,
  timeOptions,
  energyOptions,
  feelOptions,
  onNext,
  onShowHelp,
  _hasSelectedGames,
}) => {

  const players = preferences.players || 2;
  const resolvedFeelOptions = feelOptions || FEEL_OPTIONS;

  const setPlayers = useCallback((count) => {
    const clamped = Math.min(MAX_PLAYERS, Math.max(MIN_PLAYERS, count));
    setPreferences({ ...preferences, players: clamped });
  }, [preferences, setPreferences]);

  const handlePlayerDecrement = () => setPlayers(players - 1);
  const handlePlayerIncrement = () => setPlayers(players + 1);

  const isNextDisabled =
    !preferences.players ||
    !preferences.time ||
    !preferences.energy ||
    !preferences.feel;

  return (
    <div className="preferences-container">
      {/* Help Button */}
      <button
        type="button"
        className="help-button"
        onClick={onShowHelp}
        title="Show Simplify help"
        aria-label="Show help"
      >
        <HelpCircle size={20} aria-hidden="true" />
      </button>

      {/* Header */}
      <div className="preferences-header">
        <h2>Set the scene</h2>
      </div>

      {/* ── Players ── */}
      <div className="preference-group">
        <label id="spp-players-label">
          <Users size={16} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: "0.35rem" }} />
          Players
          <span
            key={players}
            className="spp-player-count"
            aria-live="polite"
          >
            {players} {players === 1 ? "player" : "players"}
          </span>
        </label>

        <div className="spp-players-row">
          <button
            type="button"
            className="spp-stepper__btn"
            onClick={handlePlayerDecrement}
            disabled={players <= MIN_PLAYERS}
            aria-label="Decrease player count"
          >
            −
          </button>

          <div className="spp-player-presets" aria-label={`${players} player${players === 1 ? "" : "s"}`}>
            {Array.from({ length: players }, (_, i) => (
              <User key={i} size={18} aria-hidden="true" />
            ))}
          </div>
          <button
            type="button"
            className="spp-stepper__btn"
            onClick={handlePlayerIncrement}
            disabled={players >= MAX_PLAYERS}
            aria-label="Increase player count"
          >
            +
          </button>
        </div>{/* /spp-players-row */}
      </div>

      {/* ── Interaction ── */}
      <div className="preference-group">
        <label id="spp-feel-label">
          <MessageCircle size={16} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: "0.35rem" }} />
          Interaction
        </label>
        <InteractionSlider
          options={resolvedFeelOptions}
          value={preferences.feel}
          onChange={(feel) => setPreferences({ ...preferences, feel })}
          ariaLabelledBy="spp-feel-label"
        />
      </div>

      {/* ── Energy Level ── */}
      <div className="preference-group">
        <label id="spp-energy-label">
          <Flame size={16} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: "0.35rem" }} />
          Energy Level
        </label>
        <div className="spp-options-row" role="group" aria-labelledby="spp-energy-label">
          {energyOptions.map((option) => (
            <IconOptionButton
              key={option.value}
              icon={ENERGY_ICONS[option.value]}
              label={option.label}
              isSelected={preferences.energy === option.value}
              onClick={() => setPreferences({ ...preferences, energy: option.value })}
              ariaLabel={`Energy: ${option.label}`}
            />
          ))}
        </div>
      </div>

      {/* ── Playtime ── */}
      <div className="preference-group">
        <label id="spp-time-label">
          <Coffee size={16} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: "0.35rem" }} />
          Playtime
        </label>
        <div className="spp-options-row" role="group" aria-labelledby="spp-time-label">
          {timeOptions.map((option) => (
            <IconOptionButton
              key={option.value}
              icon={TIME_ICONS[option.value]}
              label={option.label}
              isSelected={preferences.time === option.value}
              onClick={() => setPreferences({ ...preferences, time: option.value })}
              ariaLabel={`Playtime: ${option.label}`}
            />
          ))}
        </div>
      </div>

      {/* ── Progress indicator ── */}
      <div className="spp-progress" aria-label="Step 1 of 3: Set preferences">
        <div className="spp-progress-bar">
          <div className="spp-progress-bar__fill" style={{ width: "33%" }} />
        </div>
        <span className="spp-progress-label">Step 1 of 3: Set preferences</span>
      </div>

      {/* ── Next Button ── */}
      <button
        className="next-button"
        onClick={onNext}
        disabled={isNextDisabled}
        aria-label="Next: Select games"
      >
        Next: Select games →
      </button>
    </div>
  );
};

export default SimplifyPreferencesPanel;
